/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * AlertDialog Component
 * ------------
 * @param {object} props
 *  - open (boolean): Show/hide dialog
 *  - title (string): Dialog title
 *  - message (string): Dialog message
 *  - type (string): "error" | "success" | "warning" | "info" (default: "info")
 *  - okLabel (string): Text for Ok button (default: "OK")
 *  - cancelLabel (string): Text for Cancel button (default: "Cancel")
 *  - showCancel (boolean): Show Cancel button (default: true)
 *  - onOk (function): Ok callback
 *  - onCancel (function): Cancel callback
 *  - mini_mode (boolean): Show compact mini dialog (default: false)
 */

import React from "react";
import { useTheme } from "../../theme/ThemeContext";
import Button from "../Button";

export default function AlertDialog({
    open,
    title,
    message,
    type = "info",
    okLabel = "OK",
    cancelLabel = "Cancel",
    showCancel = true,
    onOk,
    onCancel,
    mini_mode = true
}) {
    const { theme } = useTheme();

    if (!open) return null;

    const colorMap = {
        error: theme.primary,
        success: theme.primary,
        warning: theme.primary,
        info: theme.primary,
    };

    const iconColorMap = {
        error: "red",
        success: "green",
        warning: "orange",
        info: theme.primary,
    };

    const iconMap = {
        error: "bi bi-exclamation-octagon",
        success: "bi bi-check-circle",
        warning: "bi bi-exclamation-triangle",
        info: "bi bi-info-circle",
    };

    const boxColor = colorMap[type] || theme.primary;
    const boxIconColor = iconColorMap[type] || iconColorMap["info"];
    const boxIcon = iconMap[type] || iconMap["info"];
    const gradientBorder = `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})`;

    // Styles depend on mini_mode flag
    const containerStyles = mini_mode
        ? {
            minWidth: 200,
            width: "60vw",
            maxWidth: 250,
            maxHeight: "50vh",
            padding: 10,
            borderRadius: theme.cardRadius,
            boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
            backgroundImage: `linear-gradient(#fff, #fff), ${gradientBorder}`,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        }
        : {
            minWidth: 320,
            width: "90vw",
            maxWidth: 400,
            maxHeight: "80vh",
            padding: 0,
            borderRadius: theme.cardRadius,
            boxShadow: "0 7px 32px rgba(0,0,0,.15)",
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
            backgroundImage: `linear-gradient(#fff, #fff), ${gradientBorder}`,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        };

    const titleContainerStyles = mini_mode
        ? {
            padding: 15,
            borderBottom: `1px solid ${boxColor}`,
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "#fafbfc",
            flexShrink: 0,
            borderTopLeftRadius: theme.cardRadius / 2,
            borderTopRightRadius: theme.cardRadius / 2,
        }
        : {
            padding: 23,
            borderBottom: `1px solid ${boxColor}`,
            display: "flex",
            alignItems: "center",
            gap: 13,
            background: "#fafbfc",
            flexShrink: 0,
            borderTopLeftRadius: theme.cardRadius,
            borderTopRightRadius: theme.cardRadius,
        };

    const messageStyles = mini_mode
        ? {
            padding: "14px 18px",
            color: boxColor,
            fontSize: 12,
            textAlign: "center",
            fontWeight: 500,
            whiteSpace: "pre-line",
            wordBreak: "break-word",
            overflowY: "auto",
            flexGrow: 1,
        }
        : {
            padding: "22px 26px",
            color: boxColor,
            fontSize: 16,
            textAlign: "center",
            fontWeight: 500,
            whiteSpace: "pre-line",
            wordBreak: "break-word",
            overflowY: "auto",
            flexGrow: 1,
        };

    const buttonStyles = mini_mode
        ? {
            background: gradientBorder,
            color: "#fff",
            fontWeight: 600,
            borderRadius: theme.buttonRadius,
            fontSize: 12,
            minWidth: 80,
            height: 32,
            border: "none",
            padding: "6px 20px",
            cursor: "pointer",
            boxShadow: "0 2px 5px rgba(60,30,120,0.07)",
        }
        : {
            background: gradientBorder,
            color: "#fff",
            fontWeight: 600,
            borderRadius: theme.buttonRadius,
            fontSize: 16,
            minWidth: 120,
            height: 44,
            border: "none",
            padding: "9px 32px",
            cursor: "pointer",
            boxShadow: "0 2px 5px rgba(60,30,120,0.07)",
        };

    const cancelButtonStyles = mini_mode
        ? {
            background: "#fff",
            color: theme.secondary,
            fontWeight: 600,
            borderRadius: theme.buttonRadius,
            fontSize: 12,
            minWidth: 80,
            height: 32,
            border: `2px solid ${theme.secondary}`,
            padding: "6px 20px",
            boxShadow: "0 2px 5px rgba(60,30,120,0.07)",
            cursor: "pointer",
        }
        : {
            background: "#fff",
            color: theme.secondary,
            fontWeight: 600,
            borderRadius: theme.buttonRadius,
            fontSize: 16,
            minWidth: 120,
            height: 44,
            border: `2px solid ${theme.secondary}`,
            padding: "9px 32px",
            boxShadow: "0 2px 5px rgba(60,30,120,0.07)",
            cursor: "pointer",
        };

    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 10000,
                background: "rgba(30,40,60,0.18)",
                backdropFilter: "blur(2px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "auto",
            }}
            aria-modal="true"
            role="dialog"
            tabIndex={-1}
        >
            <div style={containerStyles}>
                {/* Title */}
                <div style={titleContainerStyles}>
                    <i className={boxIcon} style={{ color: boxIconColor, fontSize: mini_mode ? 20 : 29 }} />
                    <span
                        style={{
                            fontWeight: 700,
                            fontSize: mini_mode ? 16 : 19,
                            letterSpacing: ".01em",
                            color: boxColor,
                        }}
                    >
                        {title}
                    </span>
                </div>

                {/* Message */}
                <div style={messageStyles}>{message}</div>

                {/* Actions */}
                <div
                    style={{
                        display: "flex",
                        gap: mini_mode ? 12 : 20,
                        justifyContent: "center",
                        alignItems: "center",
                        padding: mini_mode ? "8px 16px 12px 16px" : "16px 26px 24px 26px",
                        flexShrink: 0,
                    }}
                >
                    <Button
                        mode={mini_mode ? "primary-bold" : "primary-bold"}
                        onClick={onOk}
                        autoFocus
                        small={mini_mode}
                    >
                        {okLabel}
                    </Button>

                    {showCancel && (
                        <Button
                            mode="outlined"
                            onClick={onCancel}
                            small={mini_mode}
                        >
                            {cancelLabel}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
